import React from 'react';
import { Link } from 'react-router-dom';
import Disclaimer from '../components/Disclaimer';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, BookOpen, FileText, TrendingUp, Shield, Target } from 'lucide-react';

const GuidesPage = () => {
  const guides = [
    {
      id: 1,
      title: 'Comment créer un compte de paris en ligne',
      slug: 'creer-compte-paris-en-ligne',
      description: 'Étapes détaillées pour ouvrir votre compte chez un opérateur agréé et commencer à parier en toute sécurité.',
      icon: Shield,
    },
    {
      id: 2,
      title: 'Comment déposer de l\'argent sur un site de paris',
      slug: 'deposer-argent-site-paris',
      description: 'Découvrez toutes les méthodes de dépôt disponibles et choisissez celle qui correspond à vos besoins.',
      icon: TrendingUp,
    },
    {
      id: 3,
      title: 'Comment retirer vos gains',
      slug: 'retirer-gains-paris',
      description: 'Guide complet sur les processus de retrait, les délais et les meilleures pratiques pour récupérer vos gains rapidement.',
      icon: Target,
    },
    {
      id: 4,
      title: 'Comment comprendre les cotes',
      slug: 'comprendre-cotes-paris',
      description: 'Maîtrisez l\'art de lire et d\'interpréter les cotes pour maximiser vos chances de succès.',
      icon: FileText,
    },
    {
      id: 5,
      title: 'Comment choisir le meilleur site de paris',
      slug: 'choisir-meilleur-site-paris',
      description: 'Critères essentiels à considérer pour sélectionner l\'opérateur qui répond le mieux à vos attentes.',
      icon: BookOpen,
    },
    {
      id: 6,
      title: 'Stratégies de paris sur le football',
      slug: 'strategies-paris-football',
      description: 'Techniques avancées et conseils d\'experts pour améliorer votre taux de réussite sur les paris football.',
      icon: TrendingUp,
    },
    {
      id: 7,
      title: 'Gestion de votre bankroll',
      slug: 'gestion-bankroll-paris',
      description: 'Apprenez à gérer efficacement votre budget de paris pour une expérience durable et responsable.',
      icon: Target,
    },
    {
      id: 8,
      title: 'Cash-out et paris en direct',
      slug: 'cash-out-paris-direct',
      description: 'Exploitez les fonctionnalités de cash-out et de paris en direct pour optimiser vos gains et limiter vos pertes.',
      icon: Shield,
    },
    {
      id: 9,
      title: 'Bonus et promotions expliqués',
      slug: 'bonus-promotions-expliques',
      description: 'Tout ce que vous devez savoir sur les bonus de bienvenue, les freebets et les offres promotionnelles.',
      icon: FileText,
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Disclaimer />
      <Header />
      <div className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/"
            data-discover="true"
            className="inline-flex items-center gap-2 text-toxic-green hover:text-toxic-cyan mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
          
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 glass rounded-full border border-toxic-cyan/30">
              <BookOpen className="w-5 h-5 text-toxic-cyan" />
              <span className="text-sm text-toxic-cyan font-semibold">Guides</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-toxic-cyan to-toxic-green bg-clip-text text-transparent">
                Comment parier en ligne en France
              </span>
            </h1>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Guides complets et pratiques pour maîtriser l'art des paris sportifs en ligne
            </p>
          </div>

          {/* About our guides section */}
          <div className="glass-strong rounded-2xl p-8 md:p-12 border border-toxic-green/30 mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Comment nous rédigeons nos guides
            </h2>
            <div className="space-y-6 text-white/80 leading-relaxed">
              <p>
                Chez Rouge Bets, nous comprenons que parier en ligne peut sembler complexe, surtout lorsque vous débutez. 
                C'est pourquoi nous avons créé une collection exhaustive de guides pratiques, rédigés par des experts du secteur 
                qui connaissent intimement le fonctionnement des opérateurs agréés en France.
              </p>
              <p>
                Chaque guide que nous publions est le fruit d'un travail approfondi. Nous testons personnellement chaque processus, 
                analysons les conditions générales des opérateurs, comparons les différentes options disponibles et synthétisons 
                ces informations pour vous offrir des conseils clairs et actionnables. Notre objectif n'est pas simplement de vous 
                informer, mais de vous donner les outils nécessaires pour prendre des décisions éclairées et maximiser vos chances 
                de succès.
              </p>
              <p>
                Nous mettons un point d'honneur à rester objectifs et transparents. Nos guides ne sont pas influencés par les 
                partenariats que nous pouvons avoir avec certains opérateurs. Si une fonctionnalité présente des avantages, nous 
                le mentionnons. Si elle comporte des limites, nous le signalons également. Cette approche honnête garantit que 
                vous recevez des informations fiables sur lesquelles vous pouvez vous appuyer.
              </p>
              <p>
                Nos contenus sont régulièrement mis à jour pour refléter les évolutions du marché. Les bonus changent, les 
                fonctionnalités évoluent, les processus s'améliorent. Nous suivons ces changements de près et adaptons nos guides 
                en conséquence. Vous pouvez donc être certain que les informations que vous consultez sont à jour et pertinentes.
              </p>
              <p>
                Enfin, nous écrivons dans un langage accessible. Pas de jargon technique inutile, pas de termes complexes qui 
                nécessitent un dictionnaire. Nous expliquons chaque concept de manière claire, avec des exemples concrets et des 
                conseils pratiques que vous pouvez appliquer immédiatement. Parce que parier devrait être simple, même si la stratégie 
                derrière peut être sophistiquée.
              </p>
            </div>
          </div>

          {/* Charts section */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="glass-strong rounded-xl p-6 border border-toxic-green/30">
              <h3 className="text-xl font-bold text-white mb-4">Taux de réussite moyen</h3>
              <div className="h-48 flex items-end justify-center gap-2 mb-4">
                <div className="w-12 bg-gradient-to-t from-toxic-green/50 to-toxic-green rounded-t" style={{ height: '45%' }}></div>
                <div className="w-12 bg-gradient-to-t from-toxic-cyan/50 to-toxic-cyan rounded-t" style={{ height: '65%' }}></div>
                <div className="w-12 bg-gradient-to-t from-toxic-pink/50 to-toxic-pink rounded-t" style={{ height: '55%' }}></div>
                <div className="w-12 bg-gradient-to-t from-toxic-yellow/50 to-toxic-yellow rounded-t" style={{ height: '70%' }}></div>
                <div className="w-12 bg-gradient-to-t from-toxic-orange/50 to-toxic-orange rounded-t" style={{ height: '60%' }}></div>
              </div>
              <p className="text-sm text-white/70 text-center">Amélioration après avoir suivi nos guides</p>
            </div>

            <div className="glass-strong rounded-xl p-6 border border-toxic-cyan/30">
              <h3 className="text-xl font-bold text-white mb-4">Méthodes de dépôt préférées</h3>
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/70">Carte bancaire</span>
                  <span className="text-sm font-semibold text-toxic-cyan">68%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-toxic-cyan to-toxic-green" style={{ width: '68%' }}></div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/70">Virement</span>
                  <span className="text-sm font-semibold text-toxic-cyan">22%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-toxic-cyan to-toxic-green" style={{ width: '22%' }}></div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/70">Portefeuille électronique</span>
                  <span className="text-sm font-semibold text-toxic-cyan">10%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-toxic-cyan to-toxic-green" style={{ width: '10%' }}></div>
                </div>
              </div>
            </div>

            <div className="glass-strong rounded-xl p-6 border border-toxic-pink/30">
              <h3 className="text-xl font-bold text-white mb-4">Satisfaction des lecteurs</h3>
              <div className="flex items-center justify-center mb-4">
                <div className="relative w-32 h-32">
                  <svg className="transform -rotate-90 w-32 h-32">
                    <circle cx="64" cy="64" r="56" stroke="rgba(255,255,255,0.1)" strokeWidth="8" fill="none" />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="url(#gradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56 * 0.87} ${2 * Math.PI * 56}`}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ff00ff" />
                        <stop offset="100%" stopColor="#00ffff" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-toxic-pink">87%</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-white/70 text-center">Utilisateurs satisfaits de nos guides</p>
            </div>
          </div>

          {/* Guides grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((guide) => (
              <Link
                key={guide.id}
                to={`/guides/${guide.slug}`}
                data-discover="true"
                className="glass-strong rounded-xl p-6 border border-white/10 hover:border-toxic-green/50 transition-all hover:scale-105 group"
              >
                <div className="w-12 h-12 glass rounded-lg flex items-center justify-center mb-4 group-hover:bg-toxic-green/20 transition-colors">
                  <guide.icon className="w-6 h-6 text-toxic-green" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-toxic-green transition-colors">
                  {guide.title}
                </h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  {guide.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default GuidesPage;
